/**
 * useFetch Hook
 * A custom hook for data fetching with loading and error states
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for fetching data from an API with loading and error states
 * 
 * @param {Function} fetchFunction - The API call function to execute
 * @param {Array} dependencies - Dependencies array for useEffect (when to re-fetch)
 * @param {Object} options - Additional options
 * @param {boolean} options.initialFetch - Whether to fetch on mount (default: true)
 * @param {Object} options.initialData - Initial data (default: null)
 * @returns {Object} - { data, loading, error, refetch }
 */
export function useFetch(fetchFunction, dependencies = [], options = {}) {
  const { initialFetch = true, initialData = null } = options;
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(initialFetch);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchFunction(...args);
      
      // Our standardized API response format is { status, data, message }
      // Always expect this format from backend
      if (response.data?.status === 'success' && response.data?.data !== undefined) {
        setData(response.data.data);
      }
      // Fallback for any non-standard responses for backwards compatibility
      else if (response.data) {
        setData(response.data);
      }
      // Last resort
      else {
        setData(response);
      }
      
      return response;
    } catch (err) {
      // Handle standardized API error format: { status: 'error', message, errors }
      const errorMessage = 
        (err.response?.data?.status === 'error' && err.response?.data?.message) ?
          err.response.data.message :
          err.response?.data?.message || 
          err.message || 
          'An error occurred';
      
      // Store detailed errors if available
      const detailedErrors = err.response?.data?.errors || null;
      
      setError({ message: errorMessage, details: detailedErrors });
      console.error('Fetch error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchFunction]);

  // Use a ref to track previous dependencies for comparison
  const previousDeps = useRef(dependencies);
  
  // Check if dependencies have changed
  const depsChanged = !dependencies.every((dep, i) => {
    return previousDeps.current[i] === dep;
  });
  
  // Update the ref with current dependencies
  useEffect(() => {
    previousDeps.current = dependencies;
  }, [dependencies]);
  
  // Fetch data when dependencies change or on initial mount
  useEffect(() => {
    if (initialFetch && (depsChanged || previousDeps.current.length === 0)) {
      fetchData();
    }
  }, [fetchData, initialFetch, depsChanged]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchData,
    setData // Expose setData for manual updates
  };
}

export default useFetch;
